/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/system';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { IconButton, Stack, TextField, Typography } from '@mui/material';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';

function QuestionRadioForm({
  type,
  answers,
  onChange,
  question,
  selectedOptions,
}) {
  const [specialInstructions, setSpecialInstructions] = useState([]);
  const [specialOpen, setSpecialOpen] = useState({ optionId: 0, open: false });
  const isSelected = selectedOptions.find(
    (selectedOption) => selectedOption.parent === question.id
  );

  const handleChange = (option) => {
    const instruction = specialInstructions.find(
      (special) => special.optionId === option.id
    );

    const prevSelectedOptionWithSameParent = selectedOptions.find(
      ({ parent }) => parent === option.parent
    );

    // Find and remove all the selected children of previous selected item with the same parent
    const prevItemChildren = prevSelectedOptionWithSameParent?.child
      ? selectedOptions.reduce(
          (acc, { parent, child }) => {
            if (acc.includes(parent) && child) {
              acc.push(child);
            }

            return acc;
          },
          [prevSelectedOptionWithSameParent.child]
        )
      : [];

    const newSelected = selectedOptions
      .filter(({ parent }) => !prevItemChildren?.includes(parent))
      .filter(({ parent }) => parent !== option.parent)
      .concat({
        ...option,
        quantity: 1,
        order: question.order,
        weight: option.weight || 1,
        specialInstructions: instruction?.special || '',
      });

    onChange(newSelected);
  };

  const addInstructions = (text, optionId) => {
    const comment = { optionId, special: text };
    let changed = false;

    const instructions = specialInstructions.map((special) => {
      const updatedSpecial = { ...special };

      if (updatedSpecial.optionId === optionId) {
        updatedSpecial.special = comment.special;
        changed = true;
      }
      return updatedSpecial;
    });

    if (!changed) {
      instructions.push(comment);
    }

    setSpecialInstructions(instructions);
  };

  useEffect(() => {
    const updatedSpecial = selectedOptions.map((special) => ({
      optionId: special.id,
      special: special.specialInstructions,
    }));

    setSpecialInstructions(updatedSpecial);
  }, [selectedOptions]);

  const addInstructionToItem = (option) => {
    const instruction = specialInstructions.find(
      (special) => special.optionId === option.id
    );

    const newSelectedOptions = selectedOptions.map((selectedOption) => {
      if (selectedOption.id === instruction.optionId) {
        return {
          ...selectedOption,
          specialInstructions: instruction?.special || '',
        };
      }

      return selectedOption;
    });

    onChange(newSelectedOptions);
  };

  return (
    <Stack mt={3} spacing={3}>
      <Box>
        <Typography variant="body1" fontWeight={600} mb={1} display="inline">
          {question.text}
        </Typography>

        {question.required && !isSelected && (
          <Typography variant="body1" color="error" display="inline">
            {' '}
            * Required
          </Typography>
        )}
        {!question.required && (
          <Typography
            variant="body1"
            color="text.secondary"
            mb={1}
            display="inline"
          >
            Optional
          </Typography>
        )}
        {question.required && !isSelected ? (
          <Typography variant="body2" color="text.secondary">
            Select 1
          </Typography>
        ) : (
          <Typography variant="body2" color="secondary.main">
            Selection Confirmed
          </Typography>
        )}
        <RadioGroup
          aria-labelledby="demo-row-radio-buttons-group-label"
          name="row-radio-buttons-group"
        >
          {answers?.map((option) => {
            const selectedItem = selectedOptions.find(
              ({ parent }) => parent === option.parent
            );

            const instruction = specialInstructions.find(
              (special) => special.optionId === option.id
            );

            const isChecked = option.id === selectedItem?.id;
            const optionPrice = option.prices?.find(
              (price) => price.service === type.value
            )?.price;

            return (
              <Box key={option.id}>
                <Stack direction="row" justifyContent="space-between">
                  <Box>
                    <FormControlLabel
                      value={option.id}
                      data-test-id="add-item-button"
                      onChange={() => handleChange(option)}
                      control={<Radio checked={isChecked} />}
                      label={
                        <Box>
                          <Typography display="inline">
                            {option.text}
                          </Typography>
                          {optionPrice && (
                            <Typography display="inline" color="secondary">
                              {` (+$${optionPrice.toFixed(2)})`}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center' }}>
                    {isChecked && (
                      <IconButton
                        data-test-id="button-special-instructions"
                        onClick={() =>
                          setSpecialOpen((state) => ({
                            optionId: option.id,
                            open: !state.open,
                          }))
                        }
                      >
                        <ChatBubbleOutlineRoundedIcon color="primary" />
                      </IconButton>
                    )}
                  </Box>
                </Stack>
                <Box>
                  {selectedItem &&
                    specialOpen.optionId === option.id &&
                    specialOpen.open && (
                      <TextField
                        rows={3}
                        multiline
                        fullWidth
                        variant="outlined"
                        data-test-id="add-note"
                        defaultValue={instruction?.special || ''}
                        onBlur={() => addInstructionToItem(option)}
                        placeholder={`Add a note for ${option.text}`}
                        inputProps={{
                          'data-test-id': 'special-instructions',
                        }}
                        onChange={(e) =>
                          addInstructions(e.target.value, option.id)
                        }
                      />
                    )}
                </Box>
              </Box>
            );
          })}
        </RadioGroup>
      </Box>
    </Stack>
  );
}

QuestionRadioForm.propTypes = {
  answers: PropTypes.arrayOf(
    PropTypes.shape({
      child: PropTypes.number,
      id: PropTypes.string,
      parent: PropTypes.string,
      sort: PropTypes.number,
      text: PropTypes.string,
      prices: PropTypes.arrayOf(
        PropTypes.shape({
          service: PropTypes.string,
          price: PropTypes.number,
        })
      ),
    })
  ).isRequired,
  question: PropTypes.shape({
    format: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    order: PropTypes.number,
    required: PropTypes.bool,
    sub: PropTypes.bool,
    text: PropTypes.string,
  }).isRequired,
  selectedOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      order: PropTypes.number,
      parent: PropTypes.string,
      quantity: PropTypes.number,
      sort: PropTypes.number,
      specialInstructions: PropTypes.string,
      text: PropTypes.string,
      weight: PropTypes.number,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  type: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
    text: PropTypes.string,
  }).isRequired,
};

export default QuestionRadioForm;
